import { StyleSheet, View, ViewProps } from 'react-native'
import { Chip, FormField, Hyperlink, Select, Spacing, Text } from 'zens-storybook'
import { useTranslation } from 'react-i18next'
import {
  ProductsFilter,
  StorageKey,
  productConnectivityStates,
  productPeripheralStates,
  titleForProductConnectivityState,
  titleForProductPeripheralState,
  useBuildings,
  useFloors,
  useStore,
} from 'zens-store'
import { FloorSelect, ProductConnectivitySelect, ProductPeripheralsSelect } from 'zens-fragment'

import { FilterPanel } from '../filter'

const styles = StyleSheet.create({
  container: {
    zIndex: 10,
    marginBottom: Spacing.S,
  },
  content: {
    marginVertical: -Spacing.XS,
  },
  field: {
    flex: 1,
    minWidth: 275,
    margin: Spacing.XS,
  },
  select: {
    zIndex: 20,
  },
  value: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    margin: -Spacing.XXS,
    alignItems: 'center',
  },
  chip: {
    margin: Spacing.XXS,
  },
  clearAll: {
    marginLeft: Spacing.XS,
  },
  floor: {
    zIndex: 60,
  },
  installed: {
    zIndex: 50,
  },
  connectivity: {
    zIndex: 40,
  },
  peripherals: {
    zIndex: 30,
  },
})

interface Props extends ViewProps {
  companyId: number
  filter?: ProductsFilter
  onFilterChange?: (filter: ProductsFilter) => void
  loading?: boolean
}

const ProductFilter = (props: Props) => {
  const { companyId, style, filter, onFilterChange, loading, ...otherProps } = props
  const { t } = useTranslation()
  const { storage } = useStore()
  const buildings = useBuildings({ companyId })
  const floors = useFloors({ companyId })
  const hasFilter = Object.keys(filter || {}).length > 0

  const floorData = filter?.floorId
    ?.map((floorId) => floors.data?.find((floor) => floor.id === floorId))
    .map((floor) => {
      const building = buildings.data?.find((building) => building.id === floor?.buildingId)
      return { floor, building }
    })
    .sort(
      (a, b) =>
        a.building?.name?.localeCompare(b.building?.name || '') || (a.floor?.number || 0) - (b.floor?.number || 0),
    )
    .map(({ floor, building }) => ({
      key: 'floorId' as keyof ProductsFilter,
      value: floor?.id,
      title: `${building?.name}: ${floor?.displayName}`,
    }))

  const installedData =
    filter?.isInstalled !== undefined
      ? [
          {
            key: 'isInstalled' as keyof ProductsFilter,
            value: filter?.isInstalled,
            title: filter?.isInstalled ? t('Installed') : t('Not installed'),
          },
        ]
      : undefined

  const connectivityStateData = filter?.connectivityState
    ?.sort((a, b) => productConnectivityStates.indexOf(a) - productConnectivityStates.indexOf(b))
    .map((state) => ({
      key: 'connectivityState' as keyof ProductsFilter,
      value: state,
      title: titleForProductConnectivityState(state, t),
    }))

  const peripheralStateData = filter?.peripheralsState
    ?.sort((a, b) => productPeripheralStates.indexOf(a) - productPeripheralStates.indexOf(b))
    .map((state) => ({
      key: 'peripheralsState' as keyof ProductsFilter,
      value: state,
      title: titleForProductPeripheralState(state, t),
    }))

  const data = [
    ...(floorData || []),
    ...(installedData || []),
    ...(connectivityStateData || []),
    ...(peripheralStateData || []),
  ]

  const value = hasFilter ? (
    <View style={styles.value}>
      {data.map((item) => (
        <Chip
          key={`${item.key}-${item.value}`}
          title={item.title}
          closeable
          style={styles.chip}
          onClose={removeFilter(
            item as { key: keyof Omit<ProductsFilter, 'search'>; value: number | string | boolean; title: string },
          )}
        />
      ))}
      <Hyperlink title={t('Clear filters')} onPress={clearFilter} style={styles.clearAll} />
    </View>
  ) : undefined

  function clearFilter() {
    onFilterChange?.(filter?.search ? { search: filter.search } : {})
    storage.delete(`${companyId}:${StorageKey.ProductFilter}`, { session: true })
  }

  function removeFilter(item: {
    key: keyof Omit<ProductsFilter, 'search'>
    value: number | string | boolean
    title: string
  }) {
    return () => {
      const newFilter = { ...filter }
      if (item.key === 'isInstalled') {
        delete newFilter[item.key]
      } else {
        const newValue = newFilter[item.key]?.filter((value: number) => value !== item.value)
        if (newValue?.length) newFilter[item.key] = newValue
        else delete newFilter[item.key]
      }

      onFilterChange?.(newFilter)
      storage.set(`${companyId}:${StorageKey.ProductFilter}`, JSON.stringify(newFilter), { session: true })
    }
  }

  function updateFilter(key: keyof ProductsFilter, value: number[] | string | boolean) {
    const newFilter = { ...filter, [key]: value } as ProductsFilter
    onFilterChange?.(newFilter)
    storage.set(`${companyId}:${StorageKey.ProductFilter}`, JSON.stringify(newFilter), { session: true })
  }

  return (
    <FilterPanel
      value={value}
      style={[styles.container, style]}
      contentContainerStyle={styles.content}
      loading={loading}
      {...otherProps}
    >
      <FormField label={t('Floor')} style={[styles.field, styles.floor]}>
        <FloorSelect
          companyId={companyId}
          style={styles.select}
          groupBuilding
          placeholder={t('Any floor')}
          multiple
          renderValue={(currentValue) => (
            <Text>{t('{{count}} floors selected', { count: (currentValue as number[])?.length })}</Text>
          )}
          value={filter?.floorId}
          onChange={(value) => updateFilter('floorId', value as number[])}
        />
      </FormField>
      <FormField label={t('Installed')} style={[styles.field, styles.installed]}>
        <Select
          style={styles.select}
          placeholder={t('Installed or not installed')}
          items={[
            { value: true, title: t('Installed') },
            { value: false, title: t('Not installed') },
          ]}
          value={filter?.isInstalled}
          onSelect={(value) => updateFilter('isInstalled', value)}
          testID="ProductFilter.InstalledSelect"
        />
      </FormField>

      <FormField label={t('Connectivity')} style={[styles.field, styles.connectivity]}>
        <ProductConnectivitySelect
          style={styles.select}
          placeholder={t('Any status')}
          multiple
          renderValue={(currentValue) => (
            <Text>{t('{{count}} statuses selected', { count: (currentValue as number[])?.length })}</Text>
          )}
          value={filter?.connectivityState}
          onChange={(value) => updateFilter('connectivityState', value as number[])}
        />
      </FormField>
      <FormField label={t('Peripherals')} style={[styles.field, styles.peripherals]}>
        <ProductPeripheralsSelect
          style={styles.select}
          placeholder={t('Any status')}
          multiple
          renderValue={(currentValue) => (
            <Text>{t('{{count}} statuses selected', { count: (currentValue as number[])?.length })}</Text>
          )}
          value={filter?.peripheralsState}
          onChange={(value) => updateFilter('peripheralsState', value as number[])}
        />
      </FormField>
    </FilterPanel>
  )
}

interface SkeletonProps extends ViewProps {}

ProductFilter.Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  return <FilterPanel.Skeleton style={[styles.container, style]} {...otherProps} />
}

export default ProductFilter
